import { getAcceptLanguage, getTimezone } from "../utils/browser";
import { BASE, getCsrfToken } from "./constants";

// not correct really but ok for now, api responses are weird too
export interface CheckUserResponse {
  status?: "exists" | "conflict";
  code?: "user_absent";
  description?: string;
}

export type RegisterEmailResponse =
  | { ok: true; token: string }
  | { ok: false; code: "user_already_exists" };

const isExpectedResponse = (data: unknown): data is CheckUserResponse => {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  return obj.status === "exists" || obj.status === "conflict" || obj.code === "user_absent";
};

export type ConfirmEmailErrorCode =
  | "wrong_confirmation_code"
  | "confirmation_expired"
  | "token_not_found"
  | "wrong_token"
  | "max_confirmation_attempts_exceeded"
  | "unknown_error";

export type ConfirmEmailResponse =
  | { ok: true }
  | { ok: false; code: ConfirmEmailErrorCode | null };

export type LoginResponse =
  | { ok: true; status: number; data: unknown }
  | { ok: false; code: "wrong_credentials" };

const registerEmail = async (email: string): Promise<RegisterEmailResponse> => {
  const res = await fetch(`${BASE}/auth/email`, {
    method: "PUT",
    headers: {
      "Accept-Language": getAcceptLanguage(),
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ email }),
  });

  if (res.ok) {
    const data = await res.json();
    return { ok: true, token: data.token };
  }

  if (res.status === 400) {
    const data = await res.json();
    if (data?.code === "user_already_exists") {
      return { ok: false, code: "user_already_exists" };
    }
  }

  throw new Error("Unexpected response");
};

const login = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ 
      email, 
      password,
      timezone: getTimezone() 
    }),
    
  });

  if (res.ok) {
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      // empty or non-JSON body
    }
    return { ok: true, status: res.status, data };
  }

  if (res.status === 400) {
    const data = await res.json();
    if (data?.code === "wrong_credentials") {
      return { ok: false, code: "wrong_credentials" };
    }
  }

  throw new Error("Unexpected response");
};

const checkUser = async (email: string): Promise<CheckUserResponse> => {
  const res = await fetch(`${BASE}/auth/user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!isExpectedResponse(data)) {
    throw new Error("Unexpected response");
  }
  return data;
};

const confirmEmail = async (
  code: string,
  token: string,
): Promise<ConfirmEmailResponse> => {
  const res = await fetch(`${BASE}/auth/email/confirmation`, {
    method: "PATCH",
    headers: {
      "Accept-Language": getAcceptLanguage(),
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ code, token }),
  });

  if (res.ok) {
    return { ok: true };
  }

  if (res.status === 400) {
    const data = await res.json().catch(() => null);
    const code = data?.code ?? null;
    return { ok: false, code };
  }

  throw new Error("Unexpected response");
};

export type RegisterResponse = { ok: true } | { ok: false };

const register = async (payload: {
  first_name: string;
  last_name: string;
  password: string;
  country: string;
  token: string;
  agreed_to_newsletter: boolean;
  phone?: string;
  lang: string;
}): Promise<RegisterResponse> => {
  const res = await fetch(`${BASE}/auth/register`, {
    method: "PUT",
    headers: {
      "Accept-Language": getAcceptLanguage(),
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({
      ...payload,
      timezone: getTimezone(),
    }),
  });

  if (res.ok) {
    return { ok: true };
  }

  return { ok: false };
};

export type ResetPasswordResponse =
  | { ok: true }
  | { ok: false; code?: "user_absent" };

const resetPassword = async (email: string): Promise<ResetPasswordResponse> => {
  const res = await fetch(`${BASE}/password/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ email }),
  });
  if (res.ok) return { ok: true };
  if (res.status === 404) {
    const data = await res.json().catch(() => null);
    if (data?.code === "user_absent") return { ok: false, code: "user_absent" };
  }
  return { ok: false };
};

export type SetupPasswordResponse =
  | { ok: true }
  | { ok: false; code?: "wrong_token" | "token_expired" };

const setupPassword = async (
  token: string,
  new_password: string,
): Promise<SetupPasswordResponse> => {
  const res = await fetch(`${BASE}/password/setup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ token, new_password }),
  });
  if (res.ok) return { ok: true };
  if (res.status === 400) {
    const data = await res.json().catch(() => null);
    const code = data?.code;
    if (code === "wrong_token" || code === "token_expired") {
      return { ok: false, code };
    }
  }
  return { ok: false };
};

export interface RegisterUserInfo {
  first_name?: string;
  last_name?: string;
  phone?: string;
}

const getRegisterUserInfo = async (): Promise<RegisterUserInfo> => {
  const res = await fetch(`${BASE}/auth/register/userinfo`, {
    credentials: "same-origin",
  });
  if (res.ok) {
    return res.json();
  }
  throw new Error("user_absent");
};

export type CompleteGoogleRegistrationResponse =
  | { ok: true; redirect_uri?: string }
  | { ok: false };

const completeGoogleRegistration = async (payload: {
  first_name?: string;
  last_name?: string;
  country?: string;
  phone?: string;
  agreed_to_newsletter: boolean;
  agreed_to_terms: boolean;
  lang: string;
}): Promise<CompleteGoogleRegistrationResponse> => {
  const res = await fetch(`${BASE}/auth/register/external`, {
    method: "PUT",
    headers: {
      "Accept-Language": getAcceptLanguage(),
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({
      ...payload,
      timezone: getTimezone(),
    }),
  });
  if (res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: true, redirect_uri: data.redirect_uri };
  }
  return { ok: false };
};

const logout = async (): Promise<void> => {
  await fetch(`${BASE}/auth/logout`, {
    method: "POST",
    headers: { "X-CSRFToken": getCsrfToken() },
  });
};

export {
  checkUser,
  completeGoogleRegistration,
  confirmEmail,
  getRegisterUserInfo,
  login,
  logout,
  register,
  registerEmail,
  resetPassword,
  setupPassword,
};
